// CO 237 — Agent Readiness Check client. Calls the stateless federation scan
// endpoint (POST /public/agent-ready/scan) and returns the result for inline
// rendering. No persistence on the server, so there is no hash lookup, share
// image, or email capture — a scan runs and the result is shown on the page.
import axios from "axios";

export type CheckScore = 0 | 0.5 | 1;

export type CheckId =
  | "catalog"
  | "productSchema"
  | "inventory"
  | "cartApi"
  | "searchApi"
  | "mcp"
  | "ucp"
  | "commerceProtocol"
  | "policy"
  | "robots";

export interface CheckResult {
  score: CheckScore;
  evidence: string;
  fix: string;
}

export interface ScanResult {
  url: string;
  platform: string;
  isSpaFallback: boolean;
  total: number;
  checks: Record<CheckId, CheckResult>;
  scannedAt: string;
}

// The federation gateway serves both this SPA and the scan endpoint. In local
// dev VITE_API_BASE_URL points at :8080; in prod the SPA is served same-origin
// so an empty base resolves the call against the current origin.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export async function scanAgentReady(url: string): Promise<ScanResult> {
  try {
    const { data } = await axios.post<ScanResult & { status: string }>(
      `${API_BASE_URL}/public/agent-ready/scan`,
      { url },
      { timeout: 45_000, headers: { "Content-Type": "application/json" } },
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const message =
        (err.response.data as { message?: string })?.message ??
        "Couldn't run the scan. Please try again.";
      throw new Error(message);
    }
    throw new Error("Couldn't run the scan. Please try again.");
  }
}
